import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  ComplianceExceptionDTO,
  ExceptionReason,
  ProviderCheckRunResult,
  ProviderCheckRunDetailDTO,
  ProviderCheckSummaryDTO,
  ScanCheckFindingDTO,
} from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { CodeBlock } from '@deepguide-ai/ui/code-block'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@deepguide-ai/ui/sheet'
import { ChevronDown, ChevronRight, Loader2, Sparkles } from 'lucide-react'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { FindingLevelIcon } from './ScanCardShared'
import {
  REASON_LABELS,
  SCOPE_SUMMARY_LABEL,
  detectAutoManagedPattern,
  isFlaggedStatus,
} from './findings-exception-helpers'
import {
  invalidateResourceScopeQueries,
  ResourceScopeAddButton,
  ResourceScopeRemoveButton,
} from './ResourceScopeControls'

export interface ProviderCheckDetailSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  check: (Pick<ProviderCheckSummaryDTO, 'id' | 'name' | 'description' | 'lastRunId'> &
    Partial<Pick<ProviderCheckSummaryDTO, 'lastResult' | 'resourceServiceId' | 'controlMappings' | 'aiControlMappings' | 'slotKeys'>>) | null
  programId: string
  provider: string
  connectionId?: string | null
  runId?: string | null
  onRunIdChange?: (runId: string | null) => void
  canWrite?: boolean
}

const RUN_LABELS: Record<ProviderCheckRunResult, string> = {
  running: 'Running',
  error: 'Execution failed',
  pass: 'Passed',
  fail: 'Failed',
  review: 'Review',
  not_applicable: 'Not applicable',
  skipped: 'Skipped',
  interrupted: 'Interrupted',
}

export function RunBadge({ result }: { result: ProviderCheckRunResult }) {
  const status = result === 'pass' ? 'green' : result === 'fail' ? 'red' :
    result === 'error' || result === 'running' || result === 'review' || result === 'interrupted' ? 'amber' : 'default'
  return <Badge status={status}>{RUN_LABELS[result]}</Badge>
}

function formatRunTime(value: string) {
  return new Date(value).toLocaleString()
}

export function ProviderCheckDetailSheet({
  open,
  onOpenChange,
  check,
  programId,
  provider,
  connectionId,
  runId,
  onRunIdChange,
  canWrite = true,
}: ProviderCheckDetailSheetProps) {
  const [localRunId, setLocalRunId] = useState<string | null>(null)
  const selectedRunId = runId ?? localRunId
  const historyQuery = useQuery({
    ...trpc.checker.listCheckRunHistory.queryOptions({
      programId,
      provider,
      checkId: check?.id ?? '',
      ...(connectionId ? { connectionId } : {}),
    }),
    enabled: open && !!check?.id && !!programId,
  })
  const runs = historyQuery.data?.runs ?? []

  useEffect(() => {
    setLocalRunId(null)
  }, [check?.id])

  useEffect(() => {
    if (!open || runs.length === 0 || selectedRunId) return
    const nextRunId = runs[0].runId
    setLocalRunId(nextRunId)
    onRunIdChange?.(nextRunId)
  }, [onRunIdChange, open, runs, selectedRunId])

  const selectRun = (nextRunId: string) => {
    setLocalRunId(nextRunId)
    onRunIdChange?.(nextRunId)
  }
  const detailQuery = useQuery({
    ...trpc.checker.getCheckRunDetail.queryOptions({
      programId,
      provider,
      checkId: check?.id ?? '',
      runId: selectedRunId ?? '',
      ...(connectionId ? { connectionId } : {}),
    }),
    enabled: open && !!check?.id && !!programId && !!selectedRunId,
  })
  const invalidate = useInvalidateExceptionCaches()

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side='right' className='flex w-full flex-col gap-0 p-0 sm:max-w-2xl'>
        <SheetHeader className='border-border border-b px-6 py-4'>
          <div className='flex items-center gap-2 pr-8'>
            <SheetTitle className='text-base'>{check?.name ?? 'Check details'}</SheetTitle>
            {check ? <RunBadge result={detailQuery.data?.run.result ?? (check.lastResult === 'fail' ? 'fail' : check.lastResult === 'pass' ? 'pass' : 'review')} /> : null}
          </div>
          <SheetDescription className='font-mono text-[11px]'>{check?.id}</SheetDescription>
        </SheetHeader>

        <ScrollArea className='min-h-0 flex-1' viewClassName='[&>div]:!block' type='scroll'>
          <div className='space-y-6 px-6 py-5'>
            {check ? (
              <section className='space-y-2'>
                <h3 className='text-foreground text-xs font-semibold uppercase tracking-wide'>About this check</h3>
                <p className='text-muted-foreground text-xs leading-5'>{check.description}</p>
                <div className='text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-[11px]'>
                  {check.resourceServiceId ? <span>Service: {check.resourceServiceId}</span> : null}
                  <span>Controls: {check.controlMappings?.join(', ') || '—'}</span>
                  <span>Evidence slots: {check.slotKeys?.length ?? 0}</span>
                </div>
                {check.aiControlMappings?.length ? (
                  <div className='flex flex-wrap gap-1'>
                    {check.aiControlMappings.map((citation) => (
                      <Badge key={citation.fullId} status='blue'>{citation.frameworkLabel} {citation.id}</Badge>
                    ))}
                  </div>
                ) : null}
              </section>
            ) : null}

            <section>
              <h3 className='text-foreground mb-2 text-xs font-semibold uppercase tracking-wide'>Run history</h3>
              {historyQuery.isLoading ? (
                <Loader2 className='text-muted-foreground size-4 animate-spin' />
              ) : historyQuery.isError ? (
                <p className='text-status-red text-xs'>Failed to load run history.</p>
              ) : runs.length === 0 ? (
                <p className='text-muted-foreground text-xs'>This check has not run yet.</p>
              ) : (
                <div className='border-border divide-border overflow-hidden rounded-md border divide-y'>
                  {runs.map((run) => {
                    const active = run.runId === selectedRunId
                    return (
                      <button key={run.runId} type='button' onClick={() => selectRun(run.runId)} className={cn('flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-muted/50', active && 'bg-selected')}>
                        {active ? <ChevronDown className='size-3.5' /> : <ChevronRight className='size-3.5' />}
                        <div className='min-w-0 flex-1'>
                          <div className='text-foreground text-xs font-medium'>{formatRunTime(run.startedAt)}</div>
                          <div className='text-muted-foreground mt-0.5 text-[11px]'>
                            {run.passedCount} passed · {run.failedCount} failed · {run.reviewCount} review{run.exceptedCount ? ` · ${run.exceptedCount} excepted` : ''}{run.durationMs != null ? ` · ${run.durationMs} ms` : ''}
                          </div>
                        </div>
                        <RunBadge result={run.result} />
                      </button>
                    )
                  })}
                </div>
              )}
            </section>

            {selectedRunId ? (
              <section>
                <h3 className='text-foreground mb-2 text-xs font-semibold uppercase tracking-wide'>Run details</h3>
                {detailQuery.isLoading ? (
                  <div className='flex justify-center py-8'><Loader2 className='text-muted-foreground size-4 animate-spin' /></div>
                ) : detailQuery.isError ? (
                  <p className='text-status-red py-4 text-center text-xs'>Failed to load this run.</p>
                ) : detailQuery.data && check ? (
                  <CheckRunResults detail={detailQuery.data} check={check} provider={provider} onMutated={invalidate} canWrite={canWrite} />
                ) : <p className='text-muted-foreground text-xs'>Run details are unavailable.</p>}

              </section>
            ) : null}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}

export function CheckRunResults({ detail, check, provider, onMutated, canWrite = true }: {
  detail: ProviderCheckRunDetailDTO
  check: Pick<ProviderCheckSummaryDTO, 'id'>
  provider: string
  onMutated: () => void
  canWrite?: boolean
}) {
  return (
    <div className='space-y-3'>
      <RunBadge result={detail.run.result} />
      {detail.run.errorMessage ? <p className='text-status-red text-xs'>{detail.run.errorMessage}</p> : null}
      {detail.run.skipReason ? <p className='text-muted-foreground text-xs'>{detail.run.skipReason}</p> : null}
      {detail.run.result === 'not_applicable' ? <p className='text-muted-foreground text-xs'>No applicable resources.</p> : null}
      {detail.findings.length === 0 ? (
        <p className='text-muted-foreground text-xs'>No check results recorded for this run.</p>
      ) : (
        <ul className='space-y-1.5'>
          {detail.findings.map((finding) => (
            <FindingRow key={finding.id} finding={finding} check={check} provider={provider} onMutated={onMutated} canWrite={canWrite} />
          ))}
        </ul>
      )}
    </div>
  )
}

export function ServiceCheckResults({ check, programId, provider, connectionId, canWrite }: {
  check: ProviderCheckSummaryDTO
  programId: string
  provider: string
  connectionId: string
  canWrite: boolean
}) {
  const [open, setOpen] = useState(false)
  const invalidate = useInvalidateExceptionCaches()
  const detail = useQuery({
    ...trpc.checker.getCheckRunDetail.queryOptions({ programId, provider, connectionId, checkId: check.id, runId: check.lastRunId ?? '' }),
    enabled: open && !!programId && !!connectionId && !!check.lastRunId,
  })
  let content
  if (!check.lastRunId) content = <p className='text-muted-foreground text-xs'>This check has not run yet.</p>
  else if (detail.isLoading) content = <p className='text-muted-foreground text-xs'>Loading results…</p>
  else if (detail.isError || !detail.data) content = <p className='text-status-red text-xs'>Run details are unavailable.</p>
  else content = <CheckRunResults detail={detail.data} check={check} provider={provider} onMutated={invalidate} canWrite={canWrite} />

  return (
    <details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className='flex cursor-pointer list-none items-center justify-between gap-4 px-4 py-3'>
        <div className='min-w-0'>
          <p className='text-foreground text-xs font-medium'>{check.name}</p>
          <p className='text-muted-foreground mt-0.5 text-[10px]'>
            {check.lastRunSummary ? `${check.lastRunSummary.resultCount} results · ${formatRunTime(check.lastRunSummary.startedAt)}` : 'Never run'}
          </p>
        </div>
        {check.lastRunSummary ? <RunBadge result={check.lastRunSummary.result} /> : <Badge status='default'>Not run</Badge>}
      </summary>
      {open ? <div className='border-border border-t px-4 py-3'>{content}</div> : null}
    </details>
  )
}

function FindingRow({
  finding,
  check,
  provider,
  onMutated,
  canWrite,
}: {
  finding: ScanCheckFindingDTO
  check: Pick<ProviderCheckSummaryDTO, 'id'>
  provider: string
  onMutated: () => void
  canWrite: boolean
}) {
  const isFlagged = isFlaggedStatus(finding.status)
  const isExcepted = !!finding.exception
  const canExcept = canWrite && isFlagged && !!finding.resourceId
  const autoPattern = isFlagged ? detectAutoManagedPattern(finding) : null

  return (
    <li
      className={cn(
        'border-border min-w-0 rounded-sm border bg-surface px-3 py-2.5',
        finding.status === 'pass' && 'bg-surface-subtle',
        isExcepted && 'border-status-blue/30 bg-status-blue-bg'
      )}
    >
      <div className='flex items-start gap-2.5'>
        <FindingLevelIcon level={finding.status} />
        <div className='min-w-0 flex-1'>
          <div className='font-body text-text-secondary text-[12px] font-medium'>
            {finding.title}
          </div>
          {finding.resourceId && (
            <div className='mt-1 min-w-0'>
              <div className='text-muted-foreground text-[9px] font-semibold tracking-wide uppercase'>
                Finding resource
              </div>
              <div className='text-text-subtle mt-0.5 font-mono text-[11px] break-all'>
                {finding.resourceId}
              </div>
            </div>
          )}
          {finding.remediation && isFlagged && !isExcepted && (
            <div className='border-status-red/20 bg-status-red-bg mt-2 rounded-sm border px-3 py-2'>
              <div className='text-status-red text-[10px] font-semibold uppercase tracking-wide'>Remediation</div>
              <div className='text-text-body mt-1 text-[11px] leading-[1.5] whitespace-pre-wrap'>
                {finding.remediation}
              </div>
            </div>
          )}
          {finding.evidence ? (
            <details className='mt-2'>
              <summary className='text-muted-foreground cursor-pointer text-[11px] font-medium'>
                View evidence · sensitive values redacted
              </summary>
              <CodeBlock
                code={JSON.stringify(finding.evidence, null, 2)}
                language='json'
                filename='evidence.json'
                className='mt-2 max-h-72 overflow-auto text-[11px]'
              />
            </details>
          ) : null}
          {finding.exception && (
            <ExceptionSummary exception={finding.exception} />
          )}
          {!isExcepted && autoPattern && canExcept && (
            <AutoManagedHint
              hint={autoPattern}
              finding={finding}
              checkId={check.id}
              provider={provider}
              onMutated={onMutated}
            />
          )}
        </div>
        <div className='flex shrink-0 items-center gap-1.5'>
          {finding.severity && isFlagged && !isExcepted && (
            <SeverityBadge severity={finding.severity} />
          )}
          {canExcept && !isExcepted && (
            <ResourceScopeAddButton
              resourceIds={[finding.resourceId!]}
              resourceType={finding.resourceType}
              checkId={check.id}
              provider={provider}
              onMutated={onMutated}
            />
          )}
          {canWrite && finding.exception && (
            <ResourceScopeRemoveButton
              resourceIds={[finding.resourceId!]}
              checkId={check.id}
              provider={provider}
              level='CHECK'
              onMutated={onMutated}
            />
          )}
        </div>
      </div>
    </li>
  )
}

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-status-red-bg text-status-red-strong',
  high: 'bg-status-red-surface-subtle text-status-red-emphasis',
  medium: 'bg-status-amber-badge-surface text-status-amber-strong',
  low: 'bg-muted text-text-subtle',
  info: 'bg-muted text-text-subtle',
}

function SeverityBadge({ severity }: { severity: string }) {
  return (
    <span
      className={cn(
        'font-body shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium tracking-[0.5px] uppercase',
        SEVERITY_STYLES[severity] ?? 'bg-muted text-text-subtle'
      )}
    >
      {severity}
    </span>
  )
}

function ExceptionSummary({
  exception,
}: {
  exception: ComplianceExceptionDTO
}) {
  const isScope = exception.kind === 'SCOPE'
  return (
    <div className='font-body mt-1.5 space-y-0.5 text-[11px] leading-[1.5]'>
      <div className='font-medium text-cobalt-strong'>
        {isScope ? SCOPE_SUMMARY_LABEL : 'Excepted'} ·{' '}
        {REASON_LABELS[exception.reason]}
      </div>
      <div className='text-text-body whitespace-pre-wrap'>
        {exception.justification}
      </div>
      {exception.expiresAt && (
        <div className='text-text-subtle'>
          Expires {new Date(exception.expiresAt).toLocaleDateString()}
        </div>
      )}
    </div>
  )
}

function AutoManagedHint({
  hint,
  finding,
  checkId,
  provider,
  onMutated,
}: {
  hint: { reason: ExceptionReason; label: string; suggestion: string }
  finding: ScanCheckFindingDTO
  checkId: string
  provider: string
  onMutated: () => void
}) {
  return (
    <div className='font-body border-status-blue/30 bg-status-blue-bg text-text-body mt-1.5 flex items-start gap-1.5 rounded-sm border px-2 py-1.5 text-[11px] leading-[1.5]'>
      <Sparkles className='mt-0.5 h-3 w-3 shrink-0 text-cobalt-strong' />
      <div className='min-w-0 flex-1'>
        <span className='text-text-secondary'>{hint.suggestion}</span>{' '}
        <ResourceScopeAddButton
          resourceIds={[finding.resourceId!]}
          resourceType={finding.resourceType}
          checkId={checkId}
          provider={provider}
          presetReason={hint.reason}
          presetJustification={hint.label}
          variant='inline'
          onMutated={onMutated}
        />
      </div>
    </div>
  )
}

/**
 * Refresh every cache view that derives from compliance exceptions:
 * the per-resource findings list (drops the row from the failing
 * section), the exception list itself, and the per-check rollup that
 * drives `lastFailedCount` on the panel behind the dialog.
 */
function useInvalidateExceptionCaches() {
  const qc = useQueryClient()
  return () => {
    invalidateResourceScopeQueries(qc)
  }
}
